import type { ArticleData, ArticleFaq } from '~/graphql/generated/graphql';

import { ArticleTemplate } from '~/graphql/generated/graphql';
import { tipTapToPlainText } from '~/utils/text';

/**
 * The namespaces whose templates render an authored FAQ section on the public
 * page. Namespaces persist across template switches, so an inactive one may
 * hold retired FAQ copy — only the active template's section may be read.
 */
const FAQS_BY_TEMPLATE: Partial<Record<ArticleTemplate, (data: ArticleData) => ArticleFaq[] | undefined>> = {
  [ArticleTemplate.RankedCards]: data => data.rankedCards?.faqs,
  [ArticleTemplate.SalaryGuide]: data => data.salaryGuide?.faqs,
  [ArticleTemplate.ComparisonGuide]: data => data.comparisonGuide?.faqs,
  [ArticleTemplate.DayInTheLife]: data => data.dayInTheLife?.faqs,
  [ArticleTemplate.Explainer]: data => data.explainer?.faqs,
  [ArticleTemplate.CareerPathwayMap]: data => data.careerPathwayMap?.faqs,
};

/**
 * The FAQ pairs the active template actually renders, as plain text for
 * structured data. Empty for templates without a FAQ section and for
 * incomplete rows, so an emitted FAQPage node always mirrors visible copy.
 */
export const activeArticleFaqPairs = (data: ArticleData | null | undefined): { question: string; answer: string }[] => {
  if (!data) {
    return [];
  }

  return (FAQS_BY_TEMPLATE[data.template]?.(data) ?? [])
    .map(faq => ({ question: faq.question.trim(), answer: tipTapToPlainText(faq.answer) }))
    .filter(faq => faq.question !== '' && faq.answer !== '');
};
